import { useLayoutEffect, useRef } from 'react';
import { useApp, useUi } from '../app/context';
import { brandBox } from '../app/brand';
import { patchUi, ui } from '../app/ui';
import type { SessionState } from '../core/session';
import { runIntro, TIMING as T, type Timeline } from '../motion/intro';
import { ARRANGEMENTS, BRAND_NAME, BrandMark, brandState, ClaimPaths, partStyle, WordPaths, type BrandLayout } from './Brand';

/**
 * The stage on which the opening plays: a veil and a field of light beneath
 * the windows, and the mark at its hero size above them.
 *
 * It plays on every page load. Over an empty session it ends in the first
 * centred window; over restored work the real windows — never copies — are
 * held invisible from the first frame and rise into place as the mark leaves
 * for its corner. Their documents, selections, undo histories and geometry
 * are never touched: only how they are drawn, for a few seconds.
 *
 * Any key, composition, press, wheel or file drag ends the opening at once: everything
 * jumps to its resting state and the keystroke reaches the editor, which has
 * had the keyboard from the first frame.
 */

/** The QA clock, for frame-by-frame review in development builds. */
declare global {
  interface Window {
    __mytxtIntro?: Timeline;
  }
}

const QA = import.meta.env.DEV || import.meta.env.VITE_MYTXT_QA === '1';

export function heroRect(layout: BrandLayout, canvas: { w: number; h: number }) {
  const a = ARRANGEMENTS[layout];
  const ratio = a.w / a.h;
  const w = layout === 'lockup' ? Math.min(880, canvas.w * 0.62, canvas.h * 0.24 * ratio) : Math.min(340, canvas.w - 56);
  const h = w / ratio;
  return { x: Math.round((canvas.w - w) / 2), y: Math.round(canvas.h * 0.46 - h / 2), w: Math.round(w) };
}

/** Where an element rests, leaving out any animation currently moving it or its ancestors. */
function restingRect(el: HTMLElement): DOMRect {
  const moving = Array.from(document.querySelectorAll<HTMLElement>('.win-frame')).flatMap((f) => f.getAnimations().filter((a) => a.id === 'mytxt-intro'));
  const times = moving.map((a) => a.currentTime);
  moving.forEach((a) => (a.currentTime = Number(a.effect?.getComputedTiming().endTime ?? 0)));
  const rect = el.getBoundingClientRect();
  moving.forEach((a, i) => (a.currentTime = times[i]));
  return rect;
}

/** How the corner mark will read once the work is back — the travelling mark arrives in the same state. */
const CORNER_OPACITY = { rest: 1, yield: 0.38, covered: 0 } as const; // mirrors brand.css

/** The frames of every window except the focused one, back to front. */
function otherFrames(session: SessionState): HTMLElement[] {
  return session.windows
    .filter((w) => w.id !== session.focusedId)
    .sort((a, b) => a.z - b.z)
    .map((w) => document.querySelector<HTMLElement>(`.win[data-window-id="${w.id}"]:not(.win-ghost) > .win-frame`))
    .filter((f): f is HTMLElement => !!f);
}

export function Intro({ mode }: { mode: 'full' | 'restore' }) {
  const app = useApp();
  const compact = useUi((s) => s.compact);
  const stage = useRef<HTMLDivElement>(null);
  const brand = useRef<HTMLDivElement>(null);
  const layout: BrandLayout = compact ? 'stack' : 'lockup';
  const canvas = { w: window.innerWidth, h: window.innerHeight };
  const hero = heroRect(layout, canvas);

  useLayoutEffect(() => {
    const session = app.session.get();
    const frame = document.querySelector<HTMLElement>('.win[data-focused]:not(.win-ghost) > .win-frame');
    // On a compact screen only the focused window is the page; elsewhere the restored arrangement rises with it.
    const others = compact ? [] : otherFrames(session);
    const landingEl = document.querySelector<HTMLElement>(compact ? '.win[data-focused] .brand-slot .brand-mark' : '.brand-corner-mark');
    // The corner may be under a window when the work comes back: then the mark gives way rather than landing out of sight.
    const corner = compact ? 'rest' : brandState(brandBox.get(), session.windows.map((w) => w.rect));
    const landing = corner !== 'covered' ? (landingEl?.getBoundingClientRect() ?? null) : null;
    const returnFocus = document.activeElement as HTMLElement | null;
    const landingClaim = !landingEl || getComputedStyle(landingEl).getPropertyValue('--brand-claim').trim() !== '0';
    const tl = runIntro({ stage: stage.current, brand: brand.current, landing: landing?.width ? landing : null, landingOpacity: CORNER_OPACITY[corner], landingClaim, frame, others, compact });
    if (QA) window.__mytxtIntro = tl;
    let cancelled = false;
    // The landing place can still move as fonts arrive; aim again before setting off.
    const aim = () => !cancelled && corner !== 'covered' && landingEl?.isConnected && tl.retarget(restingRect(landingEl));
    document.fonts?.ready.then(aim);
    const aimTimer = setTimeout(aim, T.travel - 80);

    const end = () => tl.finish();
    // Only a real gesture ends the opening — not the synthetic focus the editor takes at launch.
    const key = (e: KeyboardEvent) => !e.repeat && end();
    window.addEventListener('keydown', key, true);
    window.addEventListener('compositionstart', end, true);
    window.addEventListener('pointerdown', end, true);
    window.addEventListener('wheel', end, { capture: true, passive: true });
    window.addEventListener('dragenter', end, true);
    window.addEventListener('resize', end);
    const off = () => {
      window.removeEventListener('keydown', key, true);
      window.removeEventListener('compositionstart', end, true);
      window.removeEventListener('pointerdown', end, true);
      window.removeEventListener('wheel', end, true);
      window.removeEventListener('dragenter', end, true);
      window.removeEventListener('resize', end);
    };

    tl.finished.then(() => {
      if (cancelled) return;
      off();
      patchUi({ intro: 'none' });
      // A replay hands the keyboard back to wherever it was.
      if (returnFocus && returnFocus !== document.body && returnFocus.isConnected && document.activeElement === document.body) returnFocus.focus({ preventScroll: true });
    });
    return () => {
      cancelled = true;
      clearTimeout(aimTimer);
      off();
      tl.cancel();
      if (window.__mytxtIntro === tl) delete window.__mytxtIntro;
    };
  }, [app, mode, compact]);

  return (
    <>
      <div className="intro-stage" ref={stage} data-mode={mode} aria-hidden="true">
        <div className="intro-veil" />
        <div className="intro-light" />
      </div>
      {/* Above the windows: in flight, the mark passes over them, never behind. */}
      <div className="brand-stage" ref={brand} aria-hidden="true" style={{ left: hero.x, top: hero.y, width: hero.w }}>
        <BrandMark layout={layout}>
          <div className="brand-part" data-part="word" style={partStyle(layout, 'word')}>
            <div className="brand-layer" data-layer="trace">
              <WordPaths />
            </div>
            <div className="brand-layer" data-layer="glow">
              <WordPaths />
            </div>
            <div className="brand-layer" data-layer="sharp">
              <WordPaths />
            </div>
          </div>
          <div className="brand-part" data-part="claim" style={partStyle(layout, 'claim')}>
            <div className="brand-layer" data-layer="trace">
              <ClaimPaths />
            </div>
            <div className="brand-layer" data-layer="glow">
              <ClaimPaths only="editor" />
            </div>
            <div className="brand-layer" data-layer="sharp">
              <ClaimPaths />
            </div>
          </div>
        </BrandMark>
        <span className="visually-hidden">{BRAND_NAME}</span>
      </div>
    </>
  );
}

/**
 * Play the opening again, exactly as a page load would over the current work
 * (development tool). Nothing in the session changes.
 */
export function replayIntro(session: SessionState) {
  if (ui.get().intro !== 'none') return;
  const empty = session.windows.length === 1 && !session.docs.length;
  patchUi({ intro: empty ? 'full' : 'restore', palette: false, contextMenu: null, layoutFor: null });
}
